/**
 * @file LanguagePairsReport.tsx
 * @brief Company report — language-pair usage (composes company analytics).
 *
 * @references
 * - features/company-analytics/api.ts (useCompanyLanguageStats)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Languages, ListOrdered } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { LanguagePairCell } from "@/features/reports-shared/LanguagePairCell";
import { mergeLanguagePairs } from "@/features/reports-shared/languagePairs";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
} from "@/features/reports-shared/previousRange";
import { useCompanyLanguageStats } from "@/features/company-analytics/api";
import {
  formatDurationSeconds,
} from "@/shared/utils/formatters";
import { defaultReportRange } from "../defaultRange";
import { ReportShell } from "./ReportShell";

export function LanguagePairsReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      scope: "company" as const,
      limit: 100,
    }),
    [range.startDate, range.endDate],
  );
  const report = useCompanyLanguageStats(queryRange);
  // The same-length window before, for the +/-% pill (03-10-2026).
  const prevQueryRange = useMemo(
    () => ({ ...queryRange, ...previousRange(queryRange) }),
    [queryRange],
  );
  const prevReport = useCompanyLanguageStats(prevQueryRange);
  const rows = useMemo(() => mergeLanguagePairs(report.data ?? []), [report.data]);
  const totalSessions = rows.reduce(
    (s, r) => s + Number(r.session_count || 0),
    0,
  );

  return (
    <ReportShell
      title="Language-pair usage"
      description="Company interpretation language pairs for the selected range (up to 100)."
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "language-pairs",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Language pairs"
              icon={ListOrdered}
              value={String(rows.length)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Sessions"
              icon={Languages}
              value={String(totalSessions)}
              hint={previousRangeLabel(queryRange)}
              delta={
                prevReport.data
                  ? percentChange(
                      totalSessions,
                      prevReport.data.reduce((n, r) => n + Number(r.session_count || 0), 0),
                    )
                  : undefined
              }
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Language pairs"
          description={`${totalSessions} sessions across ${rows.length} pairs.`}
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Pair",
              { label: "Sessions", align: "right" },
              { label: "Duration", align: "right" },
              { label: "Share", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row, idx) => {
              const share =
                totalSessions > 0
                  ? Math.round(
                      (Number(row.session_count || 0) / totalSessions) * 1000,
                    ) / 10
                  : 0;
              return (
                <tr
                  key={`${row.source_language}-${row.target_language}-${idx}`}
                  className="hover:bg-secondary/20"
                >
                  <td className="px-4 py-2">
                    <LanguagePairCell
                      source={row.source_language}
                      target={row.target_language}
                    />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.session_count}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatDurationSeconds(row.total_duration_seconds)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {share}%
                  </td>
                </tr>
              );
            })}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
